import i18n from '@/utils/i18n';
import { captureException } from '@/utils/sentry';
import { RestaurantEmployee } from '@sales-portal/domain/accounts/screens/employee-list/types';
import { SalesPortalRestaurantEmployeesDocument } from '@sales-portal/domain/accounts/screens/employee-list/blocs/documents/sales-portal-restaurant-employees.generated';
import { useCallback, useState } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useSalesPortalUpdateEmployeeShowPaymentsMutation } from '@/experiences/sales-portal/domain/accounts/screens/employee-list/containers/employee-edit-panel/blocs/documents/sales-portal-update-employee-show-payments.generated';

interface UseUpdateEmployeeShowPaymentsMutatorResult {
  onChange: (nextShowAppPayments: boolean) => Promise<void>;
  pending: boolean;
  showAppPayments: boolean;
}

/**
 * Mutator for toggling an employee's in-app payments tab access.
 *
 * Holds the toggle value locally and optimistically updates it before firing
 * the mutation. On failure it rolls the local state back and toasts a specific
 * error.
 *
 * @param employee - The employee whose payments-tab access is being updated
 * @returns `{ onChange, pending, showAppPayments }` -
 *   `showAppPayments` reflects the current value
 *   `onChange(nextShowAppPayments)` fires the mutation;
 *   `pending` is the in-flight flag.
 */
export const useUpdateEmployeeShowPaymentsMutator = (
  employee: RestaurantEmployee,
): UseUpdateEmployeeShowPaymentsMutatorResult => {
  const { showErrorToast } = useExperienceToasts();
  const [updateShowPayments, { loading: pending }] =
    useSalesPortalUpdateEmployeeShowPaymentsMutation();
  const [showAppPayments, setShowAppPayments] = useState(
    employee.show_app_payments_tab,
  );

  const onChange = useCallback(
    async (nextShowAppPayments: boolean) => {
      setShowAppPayments(nextShowAppPayments);
      try {
        const { data } = await updateShowPayments({
          refetchQueries: [SalesPortalRestaurantEmployeesDocument],
          variables: {
            chatEmployeeUUID: employee.chat_employee_id,
            showAppPaymentsTab: nextShowAppPayments,
          },
        });
        if (!data?.update_chats_employees_by_pk) {
          captureException(
            new Error('update_chats_employees_by_pk returned null'),
            { extra: { response: data?.update_chats_employees_by_pk } },
          );
          setShowAppPayments(!nextShowAppPayments);
          showErrorToast(
            i18n.t(
              'sales_portal.employee_edit_drawer.access.update_payments_error',
            ),
          );
        }
      } catch (error) {
        captureException(error);
        setShowAppPayments(!nextShowAppPayments);
        showErrorToast(
          i18n.t(
            'sales_portal.employee_edit_drawer.access.update_payments_error',
          ),
        );
      }
    },
    [employee.chat_employee_id, showErrorToast, updateShowPayments],
  );

  return { onChange, pending, showAppPayments };
};
